import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type ApiKey, type ApiKeyCreated } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";

export interface UseAPIKeysResult {
  apiKeys: ApiKey[];
  loading: boolean;
  error: string | null;
  /** The most recently created key's plaintext token — shown exactly once,
   * since the API never re-exposes it, the same shape useInvites' own
   * lastCreated already has. */
  lastCreated: ApiKeyCreated | null;
  create: (label: string, canPlaceCalls: boolean, canReadReports: boolean) => Promise<boolean>;
  revoke: (apiKeyId: string) => Promise<boolean>;
  dismissLastCreated: () => void;
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/** Manages the tenant's third-party API keys: list, issue, revoke. */
export function useAPIKeys(): UseAPIKeysResult {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<ApiKeyCreated | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { api_keys } = await api.listApiKeys();
      setApiKeys(api_keys);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (label: string, canPlaceCalls: boolean, canReadReports: boolean): Promise<boolean> => {
      const csrfToken = readCsrfCookie(document.cookie);
      try {
        const created = await api.createApiKey(csrfToken, label, canPlaceCalls, canReadReports);
        setLastCreated(created);
        await refresh();
        return true;
      } catch (err) {
        setError(describeError(err));
        return false;
      }
    },
    [refresh],
  );

  const revoke = useCallback(
    async (apiKeyId: string): Promise<boolean> => {
      const csrfToken = readCsrfCookie(document.cookie);
      try {
        await api.revokeApiKey(csrfToken, apiKeyId);
        await refresh();
        return true;
      } catch (err) {
        setError(describeError(err));
        return false;
      }
    },
    [refresh],
  );

  return { apiKeys, loading, error, lastCreated, create, revoke, dismissLastCreated: () => setLastCreated(null) };
}
